import React, { forwardRef } from "react";
import { useSelector } from "react-redux";
import { 
  additionalTermsItems,
  getTermDisplayNumber, 
  renderCheckbox as renderCheckboxSymbol, 
  getTermDisplayLabel 
} from "./flexIndustrialLeaseAdditionalTermsCheckboxes";

const FlexIndustrialLeaseAgreement = forwardRef(({ tenentInfo, data, onDeliveryConditionChange, onBrokerInfoChange, onAdditionalTermsChange }, ref) => {
  const { loginSuccess } = useSelector((state) => state.auth);

  // Helper Functions
  function calculateTotalAmount(list, unitRent = 0) {
    if (!Array.isArray(list)) {
      return unitRent || 0;
    }

    const total = list.reduce((sum, item) => {
      if (typeof item.amount !== "string") {
        return sum;
      }
      const amount = parseFloat(item.amount);
      if (isNaN(amount)) {
        return sum;
      }
      return sum + amount;
    }, 0);

    // Add unit rent to the total
    return total + (unitRent || 0);
  }

  // Format date as MM/DD/YYYY for display
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "—";
    }
  };

  // Calculate lease term in months (inclusive of start and end calendar months)
  // e.g. Feb 1, 2026 to Dec 31, 2026 = 11 months (Feb through Dec)
  const calculateLeaseTermMonths = (startDate, endDate) => {
    if (!startDate || !endDate) return "—";
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const months =
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth()) +
        1;
      if (months <= 0 || isNaN(months)) return "—";
      return months;
    } catch (error) {
      return "—";
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0.00";
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) return "$0.00";
    return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format square feet
  const formatSquareFeet = (size) => {
    if (!size && size !== 0) return "—";
    const num = typeof size === "string" ? parseFloat(size) : size;
    if (isNaN(num)) return "—";
    return num.toLocaleString("en-US");
  };

  // Render checkbox (☑ for checked, ☐ for unchecked)
  const renderCheckbox = (isChecked) => {
    return isChecked ? "☑" : "☐";
  };

  // Calculate Pro Rata Share percentage
  const calculateProRataShare = (unitSize, buildingTotalSize) => {
    if (!unitSize || !buildingTotalSize) return "—";
    const unitNum = typeof unitSize === "string" ? parseFloat(unitSize) : unitSize;
    const buildingNum = typeof buildingTotalSize === "string" ? parseFloat(buildingTotalSize) : buildingTotalSize;
    
    if (isNaN(unitNum) || isNaN(buildingNum) || buildingNum === 0) return "—";
    
    const percentage = (unitNum / buildingNum) * 100;
    return percentage.toFixed(2);
  };

  // Calculate base rent and additional charges separately
  const unitBaseRent = data?.unitInfo?.rent || 0;
  const additionalChargesTotal = calculateTotalAmount(data?.additionalCharges || [], 0);
  const monthlyBaseRent = unitBaseRent;
  const annualBaseRent = monthlyBaseRent * 12;
  const totalMonthlyRent = unitBaseRent + additionalChargesTotal;

  // Get delivery conditions with defaults
  const deliveryConditions = data?.deliveryConditions || {
    broomClean: false,
    hvacOperational: false,
    electricalOperational: false,
    rollUpDoorsOperational: false,
    restroomFixturesOperational: false,
  };

  // Get broker info with defaults (same pattern as delivery conditions)
  const brokerInfo = data?.brokerInfo || {
    tenantBrokerCheckbox: false,
    tenantBrokerName: '',
    landlordBrokerName: ''
  };

  // Format dates
  const effectiveDate = formatDate(data?.startDate);
  const commencementDate = formatDate(data?.startDate);
  const expirationDate = formatDate(data?.endDate);
  const initialTermMonths = calculateLeaseTermMonths(
    data?.startDate,
    data?.endDate
  );

  // Get names
  const landlordName = `${loginSuccess?.user?.firstName || ""} ${
    loginSuccess?.user?.lastName || ""
  }`.trim();
  const tenantName = `${tenentInfo?.firstName || ""} ${
    tenentInfo?.lastName || ""
  }`.trim();

  // Get property info
  const buildingName = data?.propertyInfo?.propertyName || "—";
  const unitName = data?.unitInfo?.unitName || "—";
  const premisesAddress =
    data?.propertyInfo?.completeAddress || buildingName;
  const rentableSquareFeet = formatSquareFeet(data?.unitInfo?.tenantSize || data?.unitInfo?.size);

  // Security deposit
  const securityDepositAmount = data?.securityDepositAmount
    ? formatCurrency(data.securityDepositAmount)
    : "$0.00";

  // Late charges - use nullish coalescing to allow 0 values, convert to number for display
  const graceDaysValue = data?.graceDays ?? 5;
  const graceDays = graceDaysValue === '' ? 5 : Number(graceDaysValue);
  
  const latePercentageValue = data?.latePercentage ?? 5;
  const latePercentage = latePercentageValue === '' ? 5 : Number(latePercentageValue);

  const lateFixedAmountValue = data?.lateFixedAmount ?? 500;
  const lateFixedAmount = lateFixedAmountValue === '' ? 500 : Number(lateFixedAmountValue);

  const lateInterestRateValue = data?.lateInterestRate ?? 5;
  const lateInterestRate = lateInterestRateValue === '' ? 5 : Number(lateInterestRateValue);

  // Pro Rata Share calculation (tenant size / property size) - both from unitInfo
  const proRataSharePercentage = calculateProRataShare(
    data?.unitInfo?.tenantSize,
    data?.unitInfo?.size
  );

  return (
    <div ref={ref} className="bg-white px-4 py-6 md:px-8 md:py-8" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', boxSizing: 'border-box', width: '100%', maxWidth: '190mm', minWidth: '320px', margin: '0 auto' }}>
      <style>{`
        @media print {
          .print-hide {
            display: none !important;
          }
          .print-only {
            display: inline !important;
          }
        }
        
        /* Custom radio button styling */
        .additional-terms-radio {
          width: 16px !important;
          height: 16px !important;
          cursor: pointer !important;
          accent-color: #2563eb !important;
          -webkit-appearance: radio;
          -moz-appearance: radio;
          appearance: radio;
        }
        
        .additional-terms-radio:checked {
          background-color: #2563eb !important;
          border-color: #2563eb !important;
        }
        
        .additional-terms-radio:focus {
          outline: 2px solid #3b82f6 !important;
          outline-offset: 2px !important;
        }
      `}</style>
      <div style={{ borderBottom: '2px solid #000', paddingBottom: '4px', marginBottom: '8px' }}>
        <p className="text-sm text-darkColor font-popinsBold text-center" style={{ wordBreak: 'break-word', padding: '0', margin: '0' }}>
          SMALL BAY / FLEX INDUSTRIAL LEASE AGREEMENT (NNN) — TEMPLATE
        </p>
      </div>

      <p className="mb-2 text-sm text-gray-700 leading-relaxed" style={{ wordBreak: 'break-word' }}>
        This Lease Agreement ("<strong>Lease</strong>") is entered into as of{" "}
        <strong>{effectiveDate}</strong> ("<strong>Effective Date</strong>") by
        and between <strong>{landlordName || "____________________"}</strong> (
        "<strong>Landlord</strong>") and{" "}
        <strong>{tenantName || "____________________"}</strong> (
        "<strong>Tenant</strong>").
      </p>

      {/* Section 1: Premises */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>1. Premises</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed" style={{ wordBreak: 'break-word' }}>
        Landlord leases to Tenant that certain space commonly known as{" "}
        <strong>{buildingName}</strong> ("<strong>Building</strong>"),
        Suite/Unit <strong>{unitName}</strong>, located at{" "}
        <strong>{premisesAddress}</strong> ("<strong>Premises</strong>"),
        containing approximately <strong>{rentableSquareFeet} rentable square feet</strong> (the "<strong>RSF</strong>"). RSF is an estimate; if later measured, Base Rent and other charges may be adjusted prospectively.
      </p>

      {/* Section 2: Term */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>2. Term</p>
      <ul className="text-sm text-gray-700 list-none pl-0">
        <li className="mb-0.5">
          <strong>Commencement Date:</strong> {commencementDate} (or the earlier
          of (i) Tenant opening for business, (ii) Tenant taking possession, or
          (iii) delivery of Premises).
        </li>
        <li className="mb-0.5">
          <strong>Expiration Date:</strong> {expirationDate}.
        </li>
        <li className="mb-0.5">
          <strong>Initial Term:</strong> {initialTermMonths === "—" || initialTermMonths === 0 || !initialTermMonths ? "— months" : `${initialTermMonths} months`}.
        </li>
      </ul>

      {/* Section 3: Rent */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>3. Rent</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>3.1 Base Rent.</strong> Tenant shall pay Base Rent as follows:
      </p>

      <div className="text-sm text-gray-700 mb-1 leading-relaxed mt-2" style={{ display: 'table', width: '100%' }}>
        <div style={{ display: 'table-row' }}>
          <div style={{ display: 'table-cell', width: '200px', paddingBottom: '4px' }}>
            <span className="font-semibold">Period</span>
          </div>
          <div style={{ display: 'table-cell', paddingLeft: '32px', paddingBottom: '4px' }}>
            <span className="font-semibold">Monthly Base Rent</span>
          </div>
          <div style={{ display: 'table-cell', paddingLeft: '32px', paddingBottom: '4px' }}>
            <span className="font-semibold">Annual Base Rent</span>
          </div>
        </div>
        {(() => {
          // Generate rows based on lease duration
          const leaseMonths = typeof initialTermMonths === 'number' ? initialTermMonths : 0;
          const numYears = leaseMonths > 0 ? Math.ceil(leaseMonths / 12) : 1;
          const annualIncrementPercentage = data?.annualIncrementPercentage || 0;
          
          const rows = [];
          let currentBaseRent = monthlyBaseRent;
          
          for (let year = 0; year < numYears; year++) {
            const startMonth = year * 12 + 1;
            const endMonth = Math.min((year + 1) * 12, leaseMonths);
            
            // Calculate the monthly total including additional charges
            const monthlyTotal = currentBaseRent + additionalChargesTotal;
            const annualTotal = monthlyTotal * 12;
            
            rows.push(
              <div key={year} style={{ display: 'table-row' }}>
                <div style={{ display: 'table-cell', width: '200px', paddingBottom: '2px' }}>
                  Months {startMonth}–{endMonth}
                </div>
                <div style={{ display: 'table-cell', paddingLeft: '32px', paddingBottom: '2px' }}>
                  {monthlyTotal > 0 ? formatCurrency(monthlyTotal) : "$_____"}
                </div>
                <div style={{ display: 'table-cell', paddingLeft: '32px', paddingBottom: '2px' }}>
                  {annualTotal > 0 ? formatCurrency(annualTotal) : "$_____"}
                </div>
              </div>
            );
            
            // Calculate next year's base rent (apply increase only to base rent)
            if (year < numYears - 1 && annualIncrementPercentage > 0) {
              const annualBaseRent = currentBaseRent * 12;
              const increase = annualBaseRent * (annualIncrementPercentage / 100);
              const monthlyIncrease = increase / 12;
              currentBaseRent = currentBaseRent + monthlyIncrease;
            }
          }
          
          return rows;
        })()}
      </div>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Base Rent is due <strong>in advance on the 1st day</strong> of each
        month.
        {data?.annualIncrementPercentage && data.annualIncrementPercentage > 0 && (
          <> Base Rent shall increase by <strong>{data.annualIncrementPercentage}%</strong> annually on each anniversary of the Commencement Date.</>
        )}
      </p>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>3.2 Additional Rent (NNN).</strong> In addition to Base Rent,
        Tenant shall pay as Additional Rent:
      </p>

      <ul className="text-sm text-gray-700 list-disc pl-6 mb-2 leading-relaxed">
        <li>
          Tenant's <strong>Pro Rata Share</strong> of{" "}
          <strong>Operating Expenses</strong> (including CAM),{" "}
          <strong>Real Estate Taxes</strong>, and <strong>Insurance</strong> (as
          defined below);
        </li>
        <li>Utilities and services metered or allocated to Tenant;</li>
        <li>All other amounts due under this Lease.</li>
      </ul>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>3.3 Pro Rata Share.</strong> Tenant's Pro Rata Share = Tenant Size / Property Size, currently estimated at{" "}
        <strong>{proRataSharePercentage === "—" ? "—" : `${proRataSharePercentage}%`}</strong>.
      </p>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>3.4 Late Charges / Interest.</strong> Any amount not paid
        within <strong>{graceDays} days</strong> of due date bears a late fee of{" "}
        <strong>{latePercentage}%</strong> (or ${lateFixedAmount}, whichever is greater) plus
        interest at <strong>{lateInterestRate}%</strong> per annum (or highest lawful rate).
      </p>

      {/* Section 4: Security Deposit */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>4. Security Deposit</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Tenant shall deposit <strong>{securityDepositAmount}</strong> as security
        for performance. Landlord may apply it to defaults; Tenant shall replenish
        upon demand.
      </p>

      {/* Section 5: Permitted Use */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>5. Permitted Use</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Permitted use: <strong>light industrial, warehousing, distribution, office/showroom incidental thereto</strong>, and other uses approved by Landlord in writing. No retail open to the general public without consent. Tenant shall not use the Premises for hazardous, unlawful, nuisance, or high-risk uses.
      </p>

      {/* Section 6: Condition; Delivery; Improvements */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        6. Condition; Delivery; Improvements
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>6.1 AS-IS; Delivery.</strong> Except as expressly set forth,
        Premises is leased <strong>AS-IS</strong>. Landlord shall deliver:
        (check as applicable)
      </p>

      <ul className="text-sm text-gray-700 list-none pl-6 mb-2 leading-relaxed">
        <li className="mb-0.5 flex items-center">
          <input
            type="checkbox"
            checked={deliveryConditions.broomClean || false}
            onChange={(e) => {
              if (onDeliveryConditionChange) {
                const conditions = {
                  ...deliveryConditions,
                  broomClean: e.target.checked,
                };
                onDeliveryConditionChange(conditions);
              }
            }}
            className="w-3.5 h-3.5 text-themeColor border-gray-300 rounded focus:ring-themeColor cursor-pointer mr-1.5"
          />
          <span>broom clean</span>
        </li>
        <li className="mb-0.5 flex items-center">
          <input
            type="checkbox"
            checked={deliveryConditions.hvacOperational || false}
            onChange={(e) => {
              if (onDeliveryConditionChange) {
                const conditions = {
                  ...deliveryConditions,
                  hvacOperational: e.target.checked,
                };
                onDeliveryConditionChange(conditions);
              }
            }}
            className="w-3.5 h-3.5 text-themeColor border-gray-300 rounded focus:ring-themeColor cursor-pointer mr-1.5"
          />
          <span>HVAC operational (if provided)</span>
        </li>
        <li className="mb-0.5 flex items-center">
          <input
            type="checkbox"
            checked={deliveryConditions.electricalOperational || false}
            onChange={(e) => {
              if (onDeliveryConditionChange) {
                const conditions = {
                  ...deliveryConditions,
                  electricalOperational: e.target.checked,
                };
                onDeliveryConditionChange(conditions);
              }
            }}
            className="w-3.5 h-3.5 text-themeColor border-gray-300 rounded focus:ring-themeColor cursor-pointer mr-1.5"
          />
          <span>electrical and lighting operational</span>
        </li>
        <li className="mb-0.5 flex items-center">
          <input
            type="checkbox"
            checked={deliveryConditions.rollUpDoorsOperational || false}
            onChange={(e) => {
              if (onDeliveryConditionChange) {
                const conditions = {
                  ...deliveryConditions,
                  rollUpDoorsOperational: e.target.checked,
                };
                onDeliveryConditionChange(conditions);
              }
            }}
            className="w-3.5 h-3.5 text-themeColor border-gray-300 rounded focus:ring-themeColor cursor-pointer mr-1.5"
          />
          <span>roll-up doors operational</span>
        </li>
        <li className="mb-0.5 flex items-center">
          <input
            type="checkbox"
            checked={deliveryConditions.restroomFixturesOperational || false}
            onChange={(e) => {
              if (onDeliveryConditionChange) {
                const conditions = {
                  ...deliveryConditions,
                  restroomFixturesOperational: e.target.checked,
                };
                onDeliveryConditionChange(conditions);
              }
            }}
            className="w-3.5 h-3.5 text-themeColor border-gray-300 rounded focus:ring-themeColor cursor-pointer mr-1.5"
          />
          <span>restroom fixtures operational</span>
        </li>
      </ul>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>6.2 Tenant Improvements.</strong> Any alterations require
        Landlord's prior written consent. Work must be performed by licensed
        contractors, comply with code, and be lien-free.
      </p>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>6.3 Liens.</strong> Tenant shall not permit
        mechanic's/materialmen's liens. Tenant must bond around or remove any
        lien within <strong>10 days</strong> of notice.
      </p>

      {/* Section 7: Maintenance and Repairs (NNN) */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        7. Maintenance and Repairs (NNN)
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Tenant is responsible, at its cost, for <strong>all maintenance and repairs</strong> to the Premises, including without limitation:
      </p>

      <ul className="text-sm text-gray-700 list-disc pl-6 mb-2 leading-relaxed">
        <li>interior walls, floors, doors, storefronts, windows in Premises;</li>
        <li>
          plumbing serving the Premises, interior electrical, lighting, and panels
          serving the Premises;
        </li>
        <li>roll-up doors (routine maintenance and repair);</li>
        <li>
          HVAC serving only the Premises (service contract required if applicable);
        </li>
        <li>keeping exterior doors/overhead doors secure.</li>
      </ul>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Landlord maintains <strong>structural components</strong> (foundation,
        structural walls, roof structure) and <strong>common areas</strong>, and
        may pass the cost through Operating Expenses except structural capital
        items, which are handled as stated in Operating Expenses (with permitted
        amortization).
      </p>

      {/* Section 8: Operating Expenses / CAM / Taxes / Insurance */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        8. Operating Expenses / CAM / Taxes / Insurance
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>8.1 Definitions.</strong>
      </p>

      <ul className="text-sm text-gray-700 list-disc pl-6 mb-2 leading-relaxed">
        <li>
          "Operating Expenses" include common area maintenance, landscaping,
          lighting, paving, striping, security (if any), property management fees,
          common utilities, trash for common areas, repairs, and permissible
          capital items amortized with interest at —%.
        </li>
        <li>
          "Real Estate Taxes" include ad valorem taxes and assessments.
        </li>
        <li>
          "Insurance" includes property and liability insurance carried by
          Landlord for the Building/project.
        </li>
      </ul>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>8.2 Estimates; Reconciliation.</strong> Landlord may bill monthly
        estimates; within — days after each calendar year end, Landlord provides
        a reconciliation and Tenant pays any shortfall within — days (or receives
        a credit).
      </p>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>8.3 Caps.</strong> (Optional) Controllable Operating Expenses
        increase cap: —% annually. Excludes taxes, insurance, utilities, snow/ice,
        security, capital repairs, and non-recurring items.
      </p>

      {/* Section 9: Utilities */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>9. Utilities</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Tenant pays all utilities serving the Premises, including electricity,
        water, sewer, gas, trash, internet/telecom. If not separately metered,
        Landlord allocates reasonably and bills as Additional Rent.
      </p>

      {/* Section 10: Insurance (Tenant) */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        10. Insurance (Tenant)
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Tenant must maintain at its expense:
      </p>

      <ul className="text-sm text-gray-700 list-disc pl-6 mb-2 leading-relaxed">
        <li>
          Commercial General Liability: <strong>$1,000,000 per occurrence / $2,000,000 aggregate</strong> (or higher as required)
        </li>
        <li>
          Property insurance on Tenant's personal property, inventory, and
          improvements
        </li>
        <li>Workers' compensation as required by law</li>
        <li>Auto liability (if vehicles used) as applicable</li>
      </ul>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Landlord and its lender/manager shall be named as <strong>Additional Insured</strong> on liability; Tenant shall provide COIs prior to occupancy and upon renewal.
      </p>

      {/* Section 11: Indemnity; Waivers */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        11. Indemnity; Waivers
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Tenant indemnifies Landlord from claims arising from Tenant's
        use/occupancy, except to the extent caused by Landlord's gross negligence
        or willful misconduct. Mutual waiver of consequential damages. Waiver of
        subrogation as permitted by insurers.
      </p>

      {/* Section 12: Compliance With Laws; Environmental */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        12. Compliance With Laws; Environmental
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Tenant shall comply with all laws, including ADA (to the extent
        applicable to Tenant's use), fire code, and environmental regulations. No
        hazardous substances except ordinary cleaning supplies in reasonable
        quantities. Tenant is responsible for spills/releases caused by Tenant and
        remediation.
      </p>

      {/* Section 13: Assignment and Subletting */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        13. Assignment and Subletting
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        No assignment or sublease without Landlord's prior written consent, which
        may be withheld in Landlord's sole discretion (or "not unreasonably
        withheld" if desired). Landlord may require financials, a fee of <strong>$300</strong>, and
        recapture rights.
      </p>

      {/* Section 14: Default and Remedies */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        14. Default and Remedies
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Events of Default include: nonpayment after notice and cure period (—
        days), failure to perform covenants after notice (— days),
        insolvency/bankruptcy, unauthorized assignment, abandonment.
      </p>

      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Landlord remedies include termination, re-entry, acceleration to the extent
        lawful, recovery of unpaid rent, reletting expenses, and attorney's fees.
      </p>

      {/* Section 15: Option to Renew (Optional) */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        15. Option to Renew (Optional)
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Tenant may renew for — additional term(s) of — months each, provided Tenant
        is not in default and gives written notice — days before expiration. Renewal
        rent: <strong>Fair Market Rent</strong> (or —% increase over last year),
        with NNN continuing.
      </p>

      {/* Section 16: Estoppel; SNDA */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>16. Estoppel; SNDA</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Tenant shall execute estoppels within <strong>10 days</strong> of request.
        Lease shall be subject and subordinate to Landlord's financing; Tenant
        agrees to sign SNDA with lender.
      </p>

      {/* Section 17: Notices */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>17. Notices</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Notices must be in writing and delivered by certified mail, courier, or
        email (if permitted), to the addresses below (or updated by notice).
      </p>

      {/* Section 18: Broker */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>18. Broker</p>
      <div className="text-sm text-gray-700 mb-2 leading-relaxed">
        <span>Tenant broker: </span>
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            checked={brokerInfo.tenantBrokerCheckbox || false}
            onChange={(e) => {
              if (onBrokerInfoChange) {
                const newBrokerInfo = {
                  ...brokerInfo,
                  tenantBrokerCheckbox: e.target.checked,
                  tenantBrokerName: e.target.checked ? brokerInfo.tenantBrokerName : ''
                };
                onBrokerInfoChange(newBrokerInfo);
              }
            }}
            className="w-3.5 h-3.5 text-themeColor border-gray-300 rounded focus:ring-themeColor cursor-pointer"
          />
          <span className="ml-1.5">broker</span>
        </label>
        {brokerInfo.tenantBrokerCheckbox && (
          <span className="ml-2">
            <input 
              type="text" 
              value={brokerInfo.tenantBrokerName || ''}
              onChange={(e) => {
                if (onBrokerInfoChange) {
                  const newBrokerInfo = {
                    ...brokerInfo,
                    tenantBrokerName: e.target.value
                  };
                  onBrokerInfoChange(newBrokerInfo);
                }
              }}
              style={{ 
                border: '1px solid #E5E7EB', 
                borderRadius: '6px',
                padding: '0 12px',
                outline: 'none', 
                width: '250px',
                height: '38px',
                fontSize: '13px',
                fontFamily: 'inherit',
                backgroundColor: '#F9FAFB',
                color: '#111827',
                lineHeight: '38px',
                boxSizing: 'border-box'
              }} 
              placeholder="Broker Name..." 
            />
          </span>
        )}
      </div>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Landlord broker: <input 
          type="text" 
          value={brokerInfo.landlordBrokerName || ''}
          onChange={(e) => {
            if (onBrokerInfoChange) {
              const newBrokerInfo = {
                ...brokerInfo,
                landlordBrokerName: e.target.value
              };
              onBrokerInfoChange(newBrokerInfo);
            }
          }}
          style={{ 
            border: '1px solid #E5E7EB', 
            borderRadius: '6px',
            padding: '0 12px',
            outline: 'none', 
            width: '250px',
            height: '38px',
            fontSize: '13px',
            fontFamily: 'inherit',
            backgroundColor: '#F9FAFB',
            color: '#111827',
            lineHeight: '38px',
            boxSizing: 'border-box'
          }} 
          placeholder="Broker Name..." 
        />.
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Commission per separate agreement.
      </p>

      {/* Section 19: Miscellaneous */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>19. Miscellaneous</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        Entire agreement; amendments in writing; severability; governing law of{" "}
        <strong>United States of America</strong>; counterparts; electronic signatures;
        attorney's fees to prevailing party.
      </p>

      {/* Divider */}
      <div className="border-t border-gray-400 my-2"></div>

      {/* Exhibits */}
      <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>EXHIBITS</p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>Exhibit A:</strong> Legal description / site plan
      </p>
      <p className="text-sm text-gray-700 mb-2 leading-relaxed">
        <strong>Exhibit B:</strong> Rules and Regulations (parking, loading,
        signage, trash, noise, after-hours access)
      </p>

      {/* Divider */}
      <div className="border-t border-gray-400 my-2"></div>

      {/* Additional Terms - Maintenance Responsibility Matrix */}
      {data?.additionalTerms && data.additionalTerms.length > 0 && (
        <>
          <p className="text-base font-semibold text-gray-900" style={{ margin: '8px 0 4px 0', padding: '0' }}>
            ADDITIONAL TERMS - Maintenance Responsibility Matrix
          </p>
          <p className="text-sm text-gray-700 mb-2 leading-relaxed">
            The following items indicate the party responsible for maintenance, repair, and replacement:
          </p>
          
          <div className="text-sm mb-4">
            {/* Header Row */}
            <div className="flex items-center border-b-2 border-gray-400 pb-1 mb-1 font-semibold text-xs">
              <div className="flex-1 pr-4">
                <span>Maintenance Item</span>
              </div>
              <div className="flex" style={{ width: '180px', justifyContent: 'space-around' }}>
                <span style={{ width: '60px', textAlign: 'center' }}>N/A</span>
                <span style={{ width: '60px', textAlign: 'center' }}>Landlord</span>
                <span style={{ width: '60px', textAlign: 'center' }}>Tenant</span>
              </div>
            </div>

            {/* Data Rows */}
            {data.additionalTerms.map((term, index) => {
              // Get the static item definition to check if it has input field
              const staticItem = additionalTermsItems.find(item => item.id === term.id);
              const hasInput = staticItem?.hasInput || false;
              const displayLabel = getTermDisplayLabel(term, term.id);
              const displayNumber = getTermDisplayNumber(index);
              
              return (
                <div key={term.id} className="flex items-center border-b border-gray-200 py-1.5 text-xs">
                  <div className="flex-1 pr-4">
                    <span>
                      {displayNumber && `${displayNumber} `}
                      {hasInput ? (
                        <input
                          type="text"
                          value={term.customText || ''}
                          onChange={(e) => {
                            if (onAdditionalTermsChange) {
                              const updatedTerms = data.additionalTerms.map(t =>
                                t.id === term.id ? { ...t, customText: e.target.value } : t
                              );
                              onAdditionalTermsChange(updatedTerms);
                            }
                          }}
                          placeholder="Enter custom term..."
                          className="border border-gray-300 rounded px-2 py-0.5 w-[80%] text-xs print-hide"
                          style={{ minWidth: '200px' }}
                        />
                      ) : (
                        displayLabel
                      )}
                    </span>
                  </div>
                  <div className="flex" style={{ width: '180px', justifyContent: 'space-around' }}>
                    <div style={{ width: '60px', textAlign: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name={`term-${term.id}`}
                        checked={term.na || false}
                        onChange={(e) => {
                          if (onAdditionalTermsChange && e.target.checked) {
                            const updatedTerms = data.additionalTerms.map(t =>
                              t.id === term.id ? { ...t, na: true, landlord: false, tenant: false } : t
                            );
                            onAdditionalTermsChange(updatedTerms);
                          }
                        }}
                        className="additional-terms-radio"
                      />
                    </div>
                    
                    <div style={{ width: '60px', textAlign: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name={`term-${term.id}`}
                        checked={term.landlord || false}
                        onChange={(e) => {
                          if (onAdditionalTermsChange && e.target.checked) {
                            const updatedTerms = data.additionalTerms.map(t =>
                              t.id === term.id ? { ...t, na: false, landlord: true, tenant: false } : t
                            );
                            onAdditionalTermsChange(updatedTerms);
                          }
                        }}
                        className="additional-terms-radio"
                      />
                    </div>
                    
                    <div style={{ width: '60px', textAlign: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name={`term-${term.id}`}
                        checked={term.tenant || false}
                        onChange={(e) => {
                          if (onAdditionalTermsChange && e.target.checked) {
                            const updatedTerms = data.additionalTerms.map(t =>
                              t.id === term.id ? { ...t, na: false, landlord: false, tenant: true } : t
                            );
                            onAdditionalTermsChange(updatedTerms);
                          }
                        }}
                        className="additional-terms-radio"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-400 my-2"></div>
        </>
      )}

      {/* Signatures */}
      <p className="text-base font-semibold text-gray-900 text-center" style={{ margin: '8px 0 4px 0', padding: '0' }}>
        SIGNATURES
      </p>

      {/* Signature Images */}
      <div className="flex justify-between items-start" style={{ marginTop: '16px', pageBreakInside: 'avoid' }}>
        <div className="text-center w-[45%]">
          <div style={{ minHeight: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
            {data?.tenanteSignature?.url && (
              <img
                onContextMenu={(e) => e.preventDefault()}
                draggable="false"
                className="h-20"
                src={data?.tenanteSignature?.url}
                alt="Tenant's signature"
              />
            )}
          </div>
          <p className="text-sm text-gray-500 mb-1">Tenant's Signature</p>
          <p className="text-sm text-gray-700">
            <strong>Date:</strong> {data?.tenantSignedDate ? formatDate(data.tenantSignedDate) : '—'}
          </p>
        </div>
        <div className="text-center w-[45%]">
          <div style={{ minHeight: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
            {(data?.landlordSignature?.url || data?.eSignature?.url) && (
              <img
                onContextMenu={(e) => e.preventDefault()}
                draggable="false"
                className="h-20"
                src={data?.landlordSignature?.url || data?.eSignature?.url}
                alt="Landlord's signature"
              />
            )}
          </div>
          <p className="text-sm text-gray-500 mb-1">Landlord's Signature</p>
          <p className="text-sm text-gray-700">
            <strong>Date:</strong> {data?.landlordSignedDate ? formatDate(data.landlordSignedDate) : '—'}
          </p>
        </div>
      </div>
    </div>
  );
});

export default FlexIndustrialLeaseAgreement;
